import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  useTheme,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LoginIcon from '@mui/icons-material/Login';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Assets
import logo from "../assets/Whitelogo09122025.png";
import logoMotto from "../assets/Logo_Motto.png";

// Utils
import { getSessionId, isTokenValid, clearAuth } from '../utils/storage';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();

  const PORTAL_URL = "https://portal.workhorse.com.sg";

  // --- 1. Auth Check ---
  useEffect(() => {
    const checkUserSession = () => {
      if (isTokenValid()) {
        setIsAuthenticated(true);
      } else {
        const currentToken = getSessionId();
        if (currentToken) {
          clearAuth();
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(false);
        }
      }
    };
    checkUserSession();
  }, []);

  // --- 2. Scroll Handler ---
  useEffect(() => {
    const handleScroll = () => {
      // Only update state if value actually changes to prevent micro-renders
      const shouldBeScrolled = window.scrollY > 50;
      setIsScrolled((prev) => (prev !== shouldBeScrolled ? shouldBeScrolled : prev));
    };
    window.addEventListener("scroll", handleScroll, { passive: true }); // passive improves scroll performance
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // --- 3. Handlers (Memoized) ---
  // Use useCallback so this function doesn't change identity on re-renders
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevState) => !prevState);
  }, []);

  // Use useMemo for static data so it doesn't recalculate
  const navLinks = useMemo(() => [
    { label: "BUY VEHICLE", href: "/vehicle-listing", icon: <DirectionsCarIcon /> },
    { label: "ABOUT", href: "/about", icon: <InfoIcon /> },
    { label: "ARTICLES", href: "/articles", icon: <ArticleIcon /> },
    { label: "CONTACT US", href: "/contact-us", icon: <ContactSupportIcon /> },
  ], []);

  // Dynamic Styles
  const headerTextColor = isScrolled ? "#333" : "#fff";
  const headerBackground = isScrolled
    ? "rgba(255, 255, 255, 0.95)"
    : "linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)";
  const headerShadow = isScrolled ? "0px 4px 20px rgba(0,0,0,0.05)" : "none";

  // --- 4. DRAWER CONTENT (Memoized) ---
  // Crucial: This prevents the drawer internals from re-rendering when you scroll the main page
  const drawerContent = useMemo(() => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafafa' }}>
      {/* Drawer Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.7)' }}
        >
          <CloseIcon />
        </IconButton>

        <Box component="img" src={logo} alt="WorkHorse Logo" sx={{ width: 120, mb: 1, mt: 2 }} />
        <Typography variant="caption" sx={{ letterSpacing: 2, opacity: 0.8, fontSize: '0.65rem', fontWeight: 600 }}>
          PREMIUM VEHICLE TRADING
        </Typography>
      </Box>

      {/* Navigation List */}
      <List sx={{ pt: 2, px: 2, flexGrow: 1 }}>
        {navLinks.map((link) => (
          <ListItem key={link.label} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component="a"
              href={link.href}
              // Note: We access the state setter directly via closure or pass a handler if needed
              // Here we just use the href behavior, but if you want SPA nav:
              // onClick={() => { navigate(link.href); handleDrawerToggle(); }}
              sx={{
                borderRadius: '12px',
                '&:hover': { bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', color: '#ffaa00' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: '#757575' }}>{link.icon}</ListItemIcon>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem', color: '#333' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Drawer Footer */}
      <Box p={3} sx={{ bgcolor: '#fff', borderTop: '1px solid #eee' }}>
        {isAuthenticated ? (
          <Button
            component="a"
            href={PORTAL_URL}
            variant="contained"
            fullWidth
            startIcon={<AdminPanelSettingsIcon />}
            sx={{
              mb: 2,
              py: 1.5,
              borderRadius: '50px',
              fontWeight: 'bold',
              background: '#FFB800',
              color: '#fff',
              boxShadow: '0 3px 5px 2px rgba(255, 165, 0, .2)',
              '&:hover': { background: '#FFC933' }
            }}
          >
            Manage Account
          </Button>
        ) : (
          <Button
            variant="contained"
            href="/login"
            fullWidth
            startIcon={<LoginIcon />}
            sx={{
              py: 1.5,
              borderRadius: '50px',
              background: 'linear-gradient(45deg, #FFB800 30%, #FF8E00 90%)',
              color: "#fff",
              fontWeight: "bold",
              boxShadow: '0 3px 5px 2px rgba(255, 165, 0, .2)',
            }}
          >
            Portal Login
          </Button>
        )}
      </Box>
    </Box>
  ), [isAuthenticated, handleDrawerToggle, navLinks, PORTAL_URL]); 
  // ^ Dependency Array: Only re-render drawer if auth changes, not scroll.

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0, left: 0, width: "100%", zIndex: 1100,
          px: { xs: 2, md: 6 },
          py: isScrolled ? 1.5 : 2.5,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: headerBackground,
          backdropFilter: isScrolled ? "blur(10px)" : "none",
          boxShadow: headerShadow,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          // Hardware acceleration for the header itself
          transform: 'translateZ(0)',
          willChange: 'background, padding',
        }}
      >
        {/* LOGO */}
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src={isScrolled ? logoMotto : logo}
            alt="WorkHorse Logo"
            onClick={() => navigate("/")}
            sx={{
              width: { xs: 130, sm: 150, md: 180 },
              height: "auto",
              cursor: "pointer",
              objectFit: "contain",
              transition: "width 0.3s ease-in-out",
            }}
          />
        </Box>

        {/* --- DESKTOP NAV --- */}
        <Stack
          direction="row" spacing={1} alignItems="center"
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          {navLinks.map((link) => (
            <Button
              key={link.label}
              variant="text"
              href={link.href}
              sx={{
                color: headerTextColor,
                textTransform: "uppercase",
                fontFamily: '"Roboto", sans-serif',
                fontSize: "0.85rem",
                fontWeight: 700,
                letterSpacing: "0.5px",
                px: 2, py: 1, borderRadius: "30px",
                position: 'relative', overflow: 'hidden', transition: "all 0.3s ease",
                "&:hover": {
                  color: isScrolled ? "#FF8E00" : "#fff",
                  backgroundColor: isScrolled ? "rgba(255, 142, 0, 0.08)" : "rgba(255, 255, 255, 0.15)",
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {link.label}
            </Button>
          ))}

          {isAuthenticated ? (
            <Button
              variant="contained"
              href={PORTAL_URL}
              startIcon={<AdminPanelSettingsIcon />}
              sx={{
                ml: 2, px: 3, py: 1, borderRadius: "50px",
                background: "#FFB800", color: isScrolled ? "#000000" : "#fff", fontWeight: 800,
                boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.39)",
                "&:hover": { background: "#FFC933" }
              }}
            >
              Manage Account
            </Button>
          ) : (
            <Button
              variant="contained"
              href="/login"
              target="_blank"
              sx={{
                ml: 3, px: 4, py: 1, borderRadius: "50px",
                background: "#FFB800", color: isScrolled ? "#000000" : "#fff", fontWeight: 700,
                boxShadow: "0 4px 14px 0 rgba(255, 184, 0, 0.39)",
                "&:hover": { background: "#FFC933" }
              }}
            >
              Login
            </Button>
          )}

        </Stack>

        {/* MOBILE MENU ICON */}
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          sx={{
            display: { md: "none" },
            color: headerTextColor,
            backgroundColor: isScrolled ? "transparent" : "rgba(0,0,0,0.2)",
            backdropFilter: isScrolled ? "none" : "blur(4px)",
            '&:hover': { backgroundColor: isScrolled ? "rgba(0,0,0,0.05)" : "rgba(0,0,0,0.4)" }
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* MOBILE DRAWER */}
      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        // Removed keepMounted: true if it's very heavy, but usually keepMounted is faster for toggle.
        // If it's still laggy, keeping it mounted is actually better for opening speed, 
        // but bad for initial page load. We keep it standard here.
        ModalProps={{
          keepMounted: true, 
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box", width: 280,
            borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Header;